import { examinationSummary, type ClinicalExaminationView } from './clinical-examination-view';
/**
 * DOCTOR-CLINICAL-WORKSPACE-V1 -- pure view-model for the consult workspace
 * (/dashboard/doctor/consult/:appointmentId). React-free; unit-tested by
 * direct call (repo idiom: no jsdom).
 *
 * ERROR-HONESTY LAW (founder 2026-07-09): scope denials say SCOPE ("This
 * appointment belongs to another clinic.") -- never role words; server
 * 400/403/409 messages render VERBATIM (the #242 ERROR-PASSTHROUGH LAW);
 * a raw 500 internal string is NEVER surfaced.
 */

import type {
  CaseHistoryEntry,
  CaseSheetClinicalView,
  ClinicalReturnRequestView,
  ClinicalFinishReviewView,
  ClinicalPlanItemView,
  PlanItemUpdateInput,
  CaseSheetNoteSectionKeyView,
  CaseSheetUpdateInput,
  ConsultPatientBanner,
  DoctorCaseHistory,
  DoctorConsultContext,
  DoctorPreconsultView,
} from '@/dashboard/api';
import { formatMinorUnitsInr, parseInrToMinorUnits } from '@/dashboard/billing/billing-entry-view';
import { formatIstDateDdMmYyyy, formatIstShortDate, istCalendarDayYmd, istMinutesOfDay } from '@/lib/datetime-ist';
import { clinicToday } from '@/dashboard/ui/day-scope';
import {
  caseSheetNotePatch,
  type CaseSheetNotesInput,
} from '@/dashboard/clinical-workbench/case-sheet-cockpit-view';
import {
  CASE_SHEET_LABELS,
  noteSectionLabel,
} from '@/dashboard/clinical-workbench/case-sheet-labels';

// --- Route param ------------------------------------------------------------

/** Parse the :appointmentId route param. Null = malformed (redirect home --
 *  the consult NEVER offers an id-entry fallback; NO-MANUAL-ID LAW). */
export function parseConsultAppointmentId(raw: string | undefined): number | null {
  if (typeof raw !== 'string' || !/^\d+$/.test(raw)) return null;
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 && n <= Number.MAX_SAFE_INTEGER ? n : null;
}

// --- ERROR-HONESTY mapping ----------------------------------------------------

export interface ConsultLoadError {
  readonly text: string;
  /** True for a scope denial (the workspace offers "Back to my queue"). */
  readonly scopeDenied: boolean;
}

/**
 * Map a consult-context load failure to honest copy:
 *   - 403 (scope denial): the server's SCOPE sentence VERBATIM ("This
 *     appointment belongs to another clinic.") -- never role words.
 *   - 400/401/404/409: the server message VERBATIM when present (the
 *     ERROR-PASSTHROUGH LAW), else a plain fallback.
 *   - 500 / no status: a generic retry line -- NEVER a raw internal error
 *     string (the #242 LAW 3a boundary).
 */
export function consultErrorMessage(err: unknown): ConsultLoadError {
  const e =
    err !== null && typeof err === 'object'
      ? (err as { status?: unknown; message?: unknown })
      : {};
  const status = typeof e.status === 'number' ? e.status : null;
  const message =
    typeof e.message === 'string' && e.message.trim() !== ''
      ? e.message.trim()
      : null;

  if (status === 403) {
    return {
      text: message ?? 'This record is outside your scope.',
      scopeDenied: true,
    };
  }
  if (status !== null && status >= 400 && status < 500) {
    return {
      text: message ?? 'This visit could not be opened.',
      scopeDenied: false,
    };
  }
  return {
    text: 'The consult workspace could not load right now. Refresh to retry.',
    scopeDenied: false,
  };
}

// --- Patient banner derivations ----------------------------------------------

/** The banner identity line under the patient name -- ROUND-3 reference
 *  grammar: "AC-000123 <mid-dot> 34 F" (bare mono UMR; age + gender initial;
 *  mid-dot joins; only the parts that are KNOWN, never a fabricated field). */
export function bannerIdentityLine(patient: ConsultPatientBanner): string {
  const parts: string[] = [];
  if (patient.umr !== null && patient.umr !== '') parts.push(patient.umr);
  const age = patient.ageYears !== null ? String(patient.ageYears) : null;
  const g =
    patient.gender !== null && patient.gender !== ''
      ? patient.gender.charAt(0).toUpperCase()
      : null;
  const demo = [age, g].filter((v): v is string => v !== null).join(' ');
  if (demo !== '') parts.push(demo);
  return parts.join(' \u00B7 ');
}

/** The banner heading name -- NEVER "Unknown patient" (parity with the day
 *  board's dayBoardPatientLabel; adversarial-review fix). A nameless
 *  appointment reads "Name pending"; the link-note below carries the honest
 *  lead-only state. Zero phone (the wire carries none). */
export function consultBannerName(
  patient: Pick<ConsultPatientBanner, 'patientName'>,
): string {
  const name = patient.patientName?.trim();
  return name && name !== '' ? name : 'Name pending';
}

/** Preserve known safety facts while a separate intake read is unresolved.
 * An unfinished or failed read must never become an assertion of absence. */
export function consultSafetyFactLabel(
  value: string | null | undefined,
  profileAvailable: boolean,
  intakeReadKind: 'loading' | 'error' | 'unavailable' | 'ready' | undefined,
  pendingReview = false,
): string {
  const known = value?.trim() || null;
  const readStatus = intakeReadKind === 'loading' ? 'Loading intake'
    : intakeReadKind === 'error' || intakeReadKind === 'unavailable' ? 'Intake unavailable' : null;
  if (readStatus) return known ? `${known} · ${readStatus}` : readStatus;
  if (known) return known;
  if (!profileAvailable) return 'Record unavailable';
  return pendingReview ? 'Awaiting review' : 'Not asked';
}

/** Honest link-state copy for the banner (never a fake identity). SCOPE 5
 *  copy: name the state plainly and say what happens next (LAYMAN TEST). */
export function bannerLinkNote(patient: ConsultPatientBanner): string | null {
  if (patient.linked) return null;
  return 'Lead-only patient - record not linked yet. Identity fields fill in once the lead is converted to a patient.';
}

/**
 * CORE-DEMO-JOURNEY-STITCH-V1 (F3): the honest, role-level attribution line for
 * the doctor's closed-visit render -- "Recorded by <role>" (who authored the
 * clinical record). Reads ONLY the wire's `authoredByRole` (the doctor wire
 * carries no named actor / no consultActor -- surfacing a NAMED actor or the
 * "Unattributed" sentinel here would need a backend read-model field, DEFERRED).
 * Returns null when the wire has no role, so the surface never renders an empty
 * "Recorded by ." (never fabricates).
 */
export function consultRecordedByLine(
  sheet: Pick<CaseSheetClinicalView, 'authoredByRole'>,
): string | null {
  const role = sheet.authoredByRole?.trim();
  return role ? `Recorded by ${role}` : null;
}

// --- Structured sheet form ------------------------------------------------

/** The structured case-sheet form: the narrative sections PLUS the two
 *  PHI-light categorical tokens (per-clinic vocabulary) PLUS the
 *  encounter-section + follow-up fields (20260728_1). Every field maps to
 *  a case_sheets column -- no invented storage; the migration-gated fields
 *  ride the write patch ONLY when the server reports their storage live. */
export interface StructuredSheetForm extends CaseSheetNotesInput {
  readonly finishReview?: ClinicalFinishReviewView | null;
  readonly returnRequest?: ClinicalReturnRequestView | null;
  readonly examination?: ClinicalExaminationView | null;
  /** '' = not selected (stored as null); else a clinic-vocabulary token. */
  readonly chiefComplaintCategory: string;
  readonly diagnosisCategory: string;
  /** Encounter sections (20260728_1): '' = not recorded (stored as null). */
  readonly medicalDentalHistory: string;
  readonly examinationFindings: string;
  /** Follow-up recommendation: '' = none; a configured option key; or the
   *  structural 'custom' escape (which requires the note). */
  readonly followUpOption: string;
  readonly followUpNote: string;
  /** Diagnosis-grain canonical tooth codes (TOOTH-NOTATION-INTERIM-V1,
   *  20260730_1): the odontogram selection; [] = none. Rides the write patch
   *  ONLY when the server reported tooth storage live AND the selection
   *  changed (a full-set replace is never sent redundantly). */
  readonly toothRefs: readonly string[];
  /** D2 (20260815_1): the TREATMENT doctor handoff pick; null = not set
   *  (display falls back to the visit's assigned doctor). Rides the write
   *  patch ONLY when attribution storage is server-reported live AND the
   *  value changed (so an autosave can never clear a server-side stamp the
   *  form has not seen). */
  readonly treatmentDoctorId: number | null;
}

export const EMPTY_STRUCTURED_FORM: StructuredSheetForm = {
  finishReview:null,
  returnRequest: null,
  chiefComplaint: '',
  findingsDiagnosis: '',
  clinicalNote: '',
  chiefComplaintCategory: '',
  diagnosisCategory: '',
  medicalDentalHistory: '',
  examinationFindings: '',
  followUpOption: '',
  followUpNote: '',
  toothRefs: [],
  treatmentDoctorId: null,
};

/** Seed the form from a loaded sheet (or empty when none exists yet). */
export function structuredFormFromSheet(
  sheet: CaseSheetClinicalView | null,
): StructuredSheetForm {
  if (sheet === null) return EMPTY_STRUCTURED_FORM;
  return {
    finishReview:sheet.finishReview??null,
    returnRequest: sheet.returnRequest ?? null,
    chiefComplaint: sheet.chiefComplaint ?? '',
    findingsDiagnosis: sheet.findingsDiagnosis ?? '',
    clinicalNote: sheet.clinicalNote ?? '',
    chiefComplaintCategory: sheet.chiefComplaintCategory ?? '',
    diagnosisCategory: sheet.diagnosisCategory ?? '',
    medicalDentalHistory: sheet.medicalDentalHistory ?? '',
    examinationFindings: sheet.examinationFindings ?? '',
    examination: sheet.examination ?? null,
    followUpOption: sheet.followUpOption ?? '',
    followUpNote: sheet.followUpNote ?? '',
    toothRefs: (sheet.toothRefs ?? []).map((r) => r.toothCode),
    treatmentDoctorId: sheet.treatmentDoctorId ?? null,
  };
}

/**
 * Map the structured form to the case-sheet write patch: the notes transform
 * reuses THE ONE caseSheetNotePatch (blank -> null), and each category select
 * maps '' -> null / token -> token (the server validates fail-closed against
 * the clinic vocabulary -- this mapper never invents a token).
 *
 * SEVERABILITY (20260728_1): the encounter-section + follow-up fields ride
 * the patch ONLY when `includeEncounterSections` is true (the server-reported
 * encounterSectionsAvailable). Pre-apply the patch stays BYTE-IDENTICAL to
 * today's shape, so a draft save can never 42703 on a missing column.
 */
export function structuredSheetPatch(
  form: StructuredSheetForm,
  includeEncounterSections: boolean,
  // TOOTH-NOTATION-INTERIM-V1 (20260730_1): the diagnosis-grain tooth codes
  // ride the patch ONLY when the caller says so -- i.e. tooth storage is
  // server-reported live AND the selection actually changed. Pre-apply (or
  // unchanged) the patch stays BYTE-IDENTICAL to today's shape, so a draft
  // save can never 409 on the missing tooth tables.
  includeToothRefs = false,
  // D2 (20260815_1): the treatment-doctor pick rides the patch ONLY when
  // attribution storage is server-reported live AND the caller says the
  // value changed (the toothRefs severability posture: pre-apply / unchanged
  // the patch stays byte-identical, so a draft save can never 409 on the
  // missing columns or clear an unseen server-side stamp).
  includeTreatmentDoctor = false,
): Pick<
  CaseSheetUpdateInput,
  | 'chiefComplaint'
  | 'findingsDiagnosis'
  | 'clinicalNote'
  | 'chiefComplaintCategory'
  | 'diagnosisCategory'
  | 'medicalDentalHistory'
  | 'examinationFindings'
  | 'followUpOption'
  | 'followUpNote'
  | 'toothRefs'
  | 'treatmentDoctorId'
> {
  return {
    ...caseSheetNotePatch(form),
    chiefComplaintCategory:
      form.chiefComplaintCategory === '' ? null : form.chiefComplaintCategory,
    diagnosisCategory:
      form.diagnosisCategory === '' ? null : form.diagnosisCategory,
    ...(includeEncounterSections
      ? {
          medicalDentalHistory:
            form.medicalDentalHistory.trim() === ''
              ? null
              : form.medicalDentalHistory.trim(),
          examinationFindings:
            form.examinationFindings.trim() === ''
              ? null
              : form.examinationFindings.trim(),
          followUpOption:
            form.followUpOption === '' ? null : form.followUpOption,
          followUpNote:
            form.followUpNote.trim() === '' ? null : form.followUpNote.trim(),
        }
      : {}),
    ...(includeToothRefs ? { toothRefs: [...form.toothRefs] } : {}),
    ...(includeTreatmentDoctor
      ? { treatmentDoctorId: form.treatmentDoctorId }
      : {}),
  };
}

// --- Config-backed note-section grammar (SCOPE 1) ------------------------------

/** One renderable clinical note section: the CLOSED key, its display label +
 *  hint, and the FORM/SHEET field it binds to (a section is presentation
 *  grammar over case_sheets columns -- never a parallel store).
 *
 *  DOCTOR-PRECALL-POLISH-V1 (DR-23): `hint` is now a SHORT PLACEHOLDER that
 *  renders INSIDE the empty field, not a standing paragraph under the label.
 *  The full sentence it used to render survives verbatim as `guidance` and
 *  is carried as the field's tooltip -- shorter screen, zero information
 *  loss. */
export interface ConsultNoteSectionDef {
  readonly key: CaseSheetNoteSectionKeyView;
  readonly label: string;
  /** Placeholder text inside the empty field. Short by law (DR-23). */
  readonly hint: string;
  /** The full guidance sentence, rendered as the field's tooltip. */
  readonly guidance: string;
  /** The free-text form/sheet field this section reads and writes. */
  readonly field:
    | 'chiefComplaint'
    | 'medicalDentalHistory'
    | 'examinationFindings'
    | 'findingsDiagnosis'
    | 'clinicalNote';
  /** The dependent categorical select rendered inside this section (the
   *  tenant's "dependent fields" pattern), or null. */
  readonly categoryField: 'chiefComplaintCategory' | 'diagnosisCategory' | null;
  readonly rows: number;
  readonly testId: string;
}

const NOTE_SECTION_DEFS: Readonly<
  Record<CaseSheetNoteSectionKeyView, ConsultNoteSectionDef>
> = {
  chief_complaint: {
    key: 'chief_complaint',
    label: noteSectionLabel('chief_complaint'),
    hint: "Reason for today's visit",
    guidance: "The reason for today's visit - start here.",
    field: 'chiefComplaint',
    categoryField: 'chiefComplaintCategory',
    rows: 2,
    testId: 'consult-chief-complaint',
  },
  medical_dental_history: {
    key: 'medical_dental_history',
    label: noteSectionLabel('medical_dental_history'),
    hint: 'Verified today',
    guidance: 'Relevant history you verified with the patient today.',
    field: 'medicalDentalHistory',
    categoryField: null,
    rows: 3,
    testId: 'consult-medical-dental-history',
  },
  examination_findings: {
    key: 'examination_findings',
    label: noteSectionLabel('examination_findings'),
    hint: 'What you observed',
    guidance: 'What you observed on examination.',
    field: 'examinationFindings',
    categoryField: null,
    rows: 3,
    testId: 'consult-examination-findings',
  },
  diagnosis: {
    key: 'diagnosis',
    label: noteSectionLabel('diagnosis'),
    hint: 'Your diagnosis',
    guidance: 'Your diagnosis for this visit.',
    field: 'findingsDiagnosis',
    categoryField: 'diagnosisCategory',
    rows: 3,
    testId: 'consult-findings',
  },
  clinical_notes: {
    key: 'clinical_notes',
    label: noteSectionLabel('clinical_notes'),
    hint: 'Anything else',
    guidance: 'Any other notes for the record.',
    field: 'clinicalNote',
    categoryField: null,
    rows: 3,
    testId: 'consult-note',
  },
};

/** The layout an OLDER server (no noteSections on the wire) degrades to:
 *  exactly the three sections whose storage predates 20260728_1 -- today's
 *  workspace, byte-equivalent. */
const LEGACY_NOTE_SECTION_KEYS: readonly CaseSheetNoteSectionKeyView[] = [
  'chief_complaint',
  'diagnosis',
  'clinical_notes',
];

/**
 * Resolve the renderable section list from the consult context: the
 * server-resolved order (already config-validated + availability-filtered),
 * mapped onto the closed def table. Unknown keys are DROPPED (fail-safe: a
 * config token this build does not know can never render an unsafe control);
 * a missing/empty wire field degrades to the legacy three-section layout.
 */
export function consultNoteSections(
  context: Pick<DoctorConsultContext, 'noteSections'>,
): readonly ConsultNoteSectionDef[] {
  const keys =
    context.noteSections !== undefined && context.noteSections.length > 0
      ? context.noteSections
      : LEGACY_NOTE_SECTION_KEYS;
  return keys
    .filter((k): k is CaseSheetNoteSectionKeyView => k in NOTE_SECTION_DEFS)
    .map((k) => NOTE_SECTION_DEFS[k]);
}

/** The finalized read-only summary rows for the served section layout (label
 *  + recorded value + the dependent category rows in place). */
export function consultNoteSummaryRows(
  sections: readonly ConsultNoteSectionDef[],
  sheet: CaseSheetClinicalView,
): ReadonlyArray<{ readonly label: string; readonly value: string | null }> {
  const rows: Array<{ label: string; value: string | null }> = [];
  for (const s of sections) {
    rows.push({ label: s.label, value: s.field === 'examinationFindings' || s.field === 'findingsDiagnosis'
      ? [sheet[s.field]?.trim(), examinationSummary(sheet.examination)].filter(Boolean).join('\n') || null
      : sheet[s.field] ?? null });
    if (s.categoryField === 'chiefComplaintCategory') {
      rows.push({
        label: 'Complaint category',
        value: sheet.chiefComplaintCategory ?? null,
      });
    }
    if (s.categoryField === 'diagnosisCategory') {
      rows.push({
        label: 'Diagnosis category',
        value: sheet.diagnosisCategory ?? null,
      });
    }
  }
  return rows;
}

/**
 * DOCTOR-CONSULT-TRUTH-AND-GATE-CORRECTIVE-V1 (C7): the SAME row grammar as
 * consultNoteSummaryRows, derived from the LIVE draft form instead of the
 * persisted sheet -- the close confirm panel's completeness line must count
 * what the doctor currently sees (the form is fresher than the sheet during
 * the autosave debounce), never a stale snapshot. Empty string = not
 * recorded (null), matching the sheet-side null convention.
 */
export function consultDraftNoteRows(
  sections: readonly ConsultNoteSectionDef[],
  form: StructuredSheetForm,
): ReadonlyArray<{ readonly label: string; readonly value: string | null }> {
  const val = (raw: string): string | null => (raw.trim() === '' ? null : raw);
  const rows: Array<{ label: string; value: string | null }> = [];
  for (const s of sections) {
    rows.push({ label: s.label, value: s.field === 'examinationFindings' || s.field === 'findingsDiagnosis'
      ? [val(form[s.field]), examinationSummary(form.examination)].filter(Boolean).join('\n') || null
      : val(form[s.field]) });
    if (s.categoryField === 'chiefComplaintCategory') {
      rows.push({
        label: 'Complaint category',
        value: val(form.chiefComplaintCategory),
      });
    }
    if (s.categoryField === 'diagnosisCategory') {
      rows.push({
        label: 'Diagnosis category',
        value: val(form.diagnosisCategory),
      });
    }
  }
  return rows;
}

// --- Follow-up recommendation view (SCOPE 4) -----------------------------------

/** The structural free-text escape key (mirrors the backend
 *  FOLLOW_UP_CUSTOM_OPTION_KEY -- a reserved token, never configured). */
export const FOLLOW_UP_CUSTOM_KEY = 'custom';

/** Display label for a follow-up option token against the served options
 *  (a stored token that left the vocabulary renders RAW -- honest, never
 *  invented). */
export function followUpOptionLabel(
  token: string,
  options: ReadonlyArray<{ readonly key: string; readonly label: string }>,
): string {
  if (token === FOLLOW_UP_CUSTOM_KEY) return 'Other (specify below)';
  return options.find((o) => o.key === token)?.label ?? token;
}

/** The finalized read-only follow-up line: label (+ note), or null when no
 *  recommendation was recorded (the summary then says "Not recorded"). */
export function followUpSummaryLine(
  sheet: Pick<CaseSheetClinicalView, 'followUpOption' | 'followUpNote'>,
  options: ReadonlyArray<{ readonly key: string; readonly label: string }>,
): string | null {
  const token = sheet.followUpOption ?? null;
  if (token === null) return null;
  const label =
    token === FOLLOW_UP_CUSTOM_KEY
      ? (sheet.followUpNote ?? 'Other')
      : followUpOptionLabel(token, options);
  const note = sheet.followUpNote ?? null;
  return token !== FOLLOW_UP_CUSTOM_KEY && note !== null && note !== ''
    ? `${label} - ${note}`
    : label;
}

/** Client-side validity gate for the follow-up pair (mirrors the server
 *  rule: the custom escape requires a note). Used to associate the inline
 *  validation message with the note field BEFORE a save round-trip. */
export function followUpSelectionValid(
  form: Pick<StructuredSheetForm, 'followUpOption' | 'followUpNote'>,
): boolean {
  if (form.followUpOption !== FOLLOW_UP_CUSTOM_KEY) return true;
  return form.followUpNote.trim() !== '';
}

/** Re-export the follow-up section labels for the ONE dictionary call site. */
export const FOLLOW_UP_LABELS = {
  section: CASE_SHEET_LABELS.followUp,
  option: CASE_SHEET_LABELS.followUpOption,
  note: CASE_SHEET_LABELS.followUpNote,
} as const;

// --- Treatment-doctor handoff picker (DOCTOR-FINDABILITY-AT-SCALE-V1) ----------
//
// The FS-19 cross-branch handoff makes the candidate set the WHOLE org's
// active roster (253 doctors at the confirmed tenant structure), which no
// flat select can carry usably. These pure helpers apply the workspace's
// existing W-B1 search idiom (treatment-picker-view.ts) to the roster:
// the RESTING list is the visit's own clinic only, a typed query searches
// the whole wire roster, and every line of copy states what the list is --
// a scoped or capped list is never presented as complete.

/** One renderable option of the handoff select. */
export interface HandoffDoctorOption {
  readonly id: number;
  readonly label: string;
  /** True when the doctor belongs to the visit's own clinic. */
  readonly home: boolean;
}

/** The roster entry shape the consult wire serves (admin facts only). */
export interface HandoffRosterEntry {
  readonly id: number;
  readonly name: string;
  readonly clinicId: number;
  /** DOCTOR-HANDS-V1 (BUILD 2): `doctors.specialty` off the consult wire --
   *  a roster STAFFING attribute of the same class the day board already
   *  carries, never PHI. OPTIONAL so every existing caller and fixture
   *  compiles unchanged; ABSENT or null means "capability unrecorded",
   *  which never excludes the doctor (fail-open rule 2). */
  readonly specialty?: string | null;
}

function handoffOptionLabel(entry: HandoffRosterEntry, home: boolean): string {
  return home ? entry.name : `${entry.name} (other clinic)`;
}

/**
 * The select's option list. Blank query = the visit's own clinic's doctors
 * ONLY (the resting state -- the org-wide rest stays reachable by typing,
 * never dumped flat; the reach line says so). A typed query = case-
 * insensitive name matches across the WHOLE wire roster, own clinic first,
 * then name order.
 */
/**
 * DOCTOR-HANDS-V1 (BUILD 2) -- PROCEDURE CAPABILITY, client side.
 *
 * These four helpers MIRROR `lib/domain/clinical/procedure-capability.ts`
 * (the server module that owns the config contract and normalizes the map
 * the consult wire serves). They are repeated here, not imported, because
 * the frontend build resolves `@` to `frontend/src` only and no path
 * reaches the backend `lib/`.
 *
 * WHY THE DUPLICATION IS SAFE TO CARRY: nothing here is an authority
 * decision. This is a FINDABILITY filter over a roster the caller is
 * already entitled to see in full, and every branch FAILS OPEN, so the
 * worst a drift between the two copies can produce is "shows more doctors
 * than the server would have" -- never a hidden doctor. The server twin's
 * unit test and this module's own test run the SAME case table.
 */

/** THE token normalizer (mirrors normalizeCapabilityToken). */
export function normalizeCapabilityToken(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  return raw.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** One plan line's lookup key: the coded catalog wins, else the procedure
 *  name; null when neither yields a token (an unmappable line is never
 *  filtered). Mirrors procedureCapabilityKey. */
export function procedureCapabilityKey(
  catalogCode: string | null | undefined,
  procedureName: string | null | undefined,
): string | null {
  const coded = normalizeCapabilityToken(catalogCode);
  if (coded !== '') return coded;
  const named = normalizeCapabilityToken(procedureName);
  return named === '' ? null : named;
}

/** The specialties required to perform one line, from the wire's
 *  plan-scoped map. EMPTY = unmapped = unfiltered. */
export function requiredSpecialtiesForLine(
  procedureSpecialties: Readonly<Record<string, readonly string[]>> | undefined,
  catalogCode: string | null | undefined,
  procedureName: string | null | undefined,
): readonly string[] {
  if (procedureSpecialties === undefined) return [];
  const key = procedureCapabilityKey(catalogCode, procedureName);
  if (key === null) return [];
  return procedureSpecialties[key] ?? [];
}

/** Does this doctor stand for a line requiring `required`? FAIL OPEN on
 *  both unknowns: an unmapped procedure admits everyone (rule 1) and a
 *  doctor with no recorded specialty is admitted, never hidden (rule 2). */
export function doctorMeetsProcedureCapability(
  doctorSpecialty: string | null | undefined,
  required: readonly string[],
): boolean {
  if (required.length === 0) return true;
  const specialty = normalizeCapabilityToken(doctorSpecialty);
  if (specialty === '') return true;
  return required.includes(specialty);
}

/**
 * The picker's resolved roster state. `options` is what the select renders;
 * the two flags let the reach line SAY what happened, because a list that
 * silently narrowed is the DOCTOR-FINDABILITY-AT-SCALE-V1 defect all over
 * again.
 */
export interface HandoffRosterView {
  readonly options: readonly HandoffDoctorOption[];
  /** A capability requirement was active AND narrowed the resting list. */
  readonly capabilityFiltered: boolean;
  /** The requirement was active and matched NOBODY at this clinic, so it
   *  was RELEASED and the full resting list stands (never an empty list). */
  readonly capabilityReleased: boolean;
  /** How many doctors the resting clinic list holds before any capability
   *  narrowing (the honest denominator for the reach line). */
  readonly clinicCount: number;
}

/**
 * THE ONE roster derivation. Blank query = the visit's own clinic's doctors
 * ONLY (the resting state -- the org-wide rest stays reachable by typing,
 * never dumped flat; the reach line says so), NARROWED to the doctors who
 * perform this line's procedure when the org mapped it. A typed query =
 * case-insensitive name matches across the WHOLE wire roster, own clinic
 * first, then name order -- and the capability filter is DELIBERATELY NOT
 * applied there: typing a name is THE ESCAPE HATCH that reaches a doctor
 * the filter excluded (recon 5.M: "manual add allowed when a consultant is
 * genuinely needed").
 *
 * THREE fail-open fences, so the filter can never hide the right doctor:
 *   1. `required` empty (procedure unmapped) -> no narrowing at all.
 *   2. A doctor with no recorded specialty is never narrowed out.
 *   3. If the narrowing would empty the resting list, it is RELEASED and
 *      the full clinic list stands -- an empty picker is never rendered.
 */
export function handoffRosterView(
  roster: ReadonlyArray<HandoffRosterEntry>,
  visitClinicId: number | null,
  query: string,
  required: readonly string[] = [],
): HandoffRosterView {
  const q = query.trim().toLowerCase();
  const sortAndLabel = (
    pool: ReadonlyArray<HandoffRosterEntry>,
  ): readonly HandoffDoctorOption[] =>
    [...pool]
      .sort((a, b) => {
        const aHome = a.clinicId === visitClinicId ? 0 : 1;
        const bHome = b.clinicId === visitClinicId ? 0 : 1;
        if (aHome !== bHome) return aHome - bHome;
        return a.name.localeCompare(b.name);
      })
      .map((d) => {
        const home = d.clinicId === visitClinicId;
        return { id: d.id, label: handoffOptionLabel(d, home), home };
      });

  // THE ESCAPE HATCH: a typed name searches the whole wire roster and is
  // NEVER capability-filtered.
  if (q !== '') {
    return {
      options: sortAndLabel(roster.filter((d) => d.name.toLowerCase().includes(q))),
      capabilityFiltered: false,
      capabilityReleased: false,
      clinicCount: roster.filter((d) => d.clinicId === visitClinicId).length,
    };
  }

  const clinicPool = roster.filter((d) => d.clinicId === visitClinicId);
  if (required.length === 0) {
    // Fence 1: nothing mapped -> byte-identical to the pre-package resting list.
    return {
      options: sortAndLabel(clinicPool),
      capabilityFiltered: false,
      capabilityReleased: false,
      clinicCount: clinicPool.length,
    };
  }
  // Fence 3 tests the AFFIRMATIVE matches -- doctors whose specialty is
  // RECORDED and matches. It cannot be written as "the capable set is
  // empty": the capable set also holds every unrecorded-specialty doctor
  // (fence 2), so a requirement no one at this clinic actually meets would
  // otherwise survive as a list of one -- the unknown doctor shown and nine
  // recorded doctors hidden. That is precisely a filter hiding the right
  // doctor, so the release keys on the affirmative count instead.
  const affirmative = clinicPool.filter(
    (d) =>
      normalizeCapabilityToken(d.specialty) !== '' &&
      doctorMeetsProcedureCapability(d.specialty, required),
  );
  if (affirmative.length === 0) {
    return {
      options: sortAndLabel(clinicPool),
      capabilityFiltered: false,
      capabilityReleased: true,
      clinicCount: clinicPool.length,
    };
  }
  // Fence 2: the affirmative matches PLUS every doctor whose capability is
  // simply unrecorded -- unknown is never treated as incapable.
  const capable = clinicPool.filter((d) =>
    doctorMeetsProcedureCapability(d.specialty, required),
  );
  return {
    options: sortAndLabel(capable),
    capabilityFiltered: capable.length < clinicPool.length,
    capabilityReleased: false,
    clinicCount: clinicPool.length,
  };
}

/**
 * The select's option list. Blank query = the visit's own clinic's doctors
 * ONLY (the resting state -- the org-wide rest stays reachable by typing,
 * never dumped flat; the reach line says so). A typed query = case-
 * insensitive name matches across the WHOLE wire roster, own clinic first,
 * then name order.
 *
 * THE ONE implementation is handoffRosterView above; this keeps the
 * pre-package 3-argument name and shape for every existing caller and pin.
 */
export function handoffDoctorOptions(
  roster: ReadonlyArray<HandoffRosterEntry>,
  visitClinicId: number | null,
  query: string,
  required: readonly string[] = [],
): readonly HandoffDoctorOption[] {
  return handoffRosterView(roster, visitClinicId, query, required).options;
}

/**
 * The CURRENT selection's option when the filtered list does not already
 * carry it (a cross-clinic pick at rest, or a stamped doctor no longer on
 * the active roster) -- the select must always render its own value, never
 * silently display the placeholder over a real selection. Label falls back
 * to the attribution-resolved name; an id whose name never resolved renders
 * the honest numeric form rather than pretending no selection exists.
 */
export function handoffCurrentOption(
  options: readonly HandoffDoctorOption[],
  currentDoctorId: number | null,
  currentDoctorName: string | null,
): HandoffDoctorOption | null {
  if (currentDoctorId === null) return null;
  if (options.some((o) => o.id === currentDoctorId)) return null;
  return {
    id: currentDoctorId,
    label: currentDoctorName ?? `Doctor ${currentDoctorId}`,
    home: false,
  };
}

/**
 * The reach line under the handoff search box -- the sentence that keeps a
 * scoped resting list or a bounded wire from ever reading as the complete
 * universe (silence was the DOCTOR-FINDABILITY-AT-SCALE-V1 defect).
 */
export function handoffReachLine(
  rosterCount: number,
  truncated: boolean,
  query: string,
  matchCount: number,
  /** DOCTOR-HANDS-V1 (BUILD 2): the capability state of the RESTING list.
   *  Omitted -- or with both flags false -- the copy is byte-identical to
   *  the pre-package line, so the visit-grain mount and every existing pin
   *  are untouched. When a filter IS active the line must NAME the escape
   *  hatch, because a list that silently narrowed is the
   *  DOCTOR-FINDABILITY-AT-SCALE-V1 defect wearing a new hat. */
  capability?: {
    readonly filtered: boolean;
    readonly released: boolean;
    readonly clinicCount: number;
  },
): string {
  if (rosterCount === 0) return 'The doctor roster is unavailable right now.';
  const reach = truncated
    ? `Type a name to search the first ${rosterCount} chain doctors this screen received.`
    : `Type a name to search all ${rosterCount} doctors across the chain.`;
  if (query.trim() === '') {
    if (capability?.released === true) {
      // Fence 3 fired: the requirement matched nobody here, so NOTHING was
      // hidden. Say both halves -- what is shown and why.
      return `No doctor at this clinic is recorded for this procedure, so all ${capability.clinicCount} are shown. ${reach}`;
    }
    if (capability?.filtered === true) {
      return `Showing the ${matchCount} of ${capability.clinicCount} doctors at this clinic recorded for this procedure. ${reach}`;
    }
    return truncated
      ? `This clinic's doctors are shown. Type a name to search the first ${rosterCount} chain doctors this screen received.`
      : `This clinic's doctors are shown. Type a name to search all ${rosterCount} doctors across the chain.`;
  }
  if (matchCount === 0) return 'No doctor in the list matches that name.';
  return `${matchCount} of ${rosterCount} chain doctors match.`;
}

/**
 * DOCTOR-CONSULT-DELTA-V1 -- the TREATMENT-SPECIFIC doctor label for ONE plan
 * line (tracker row "Doctor Assigned (Treatment Specific)").
 *
 * The line's own assignment, resolved against the roster the consult wire
 * already carries. THREE honest states and no fourth: the roster name; the
 * bare id when an assigned doctor is no longer on the active roster (the
 * assignment still happened -- it is never dressed as unassigned); and the
 * ruled unassigned copy. The visit's own doctor is NEVER substituted -- an
 * unassigned line reads unassigned, exactly as the billing sibling renders
 * it (billLineDoctorLabel). Pure.
 */
export const PLAN_ITEM_DOCTOR_UNASSIGNED_COPY = 'Doctor not assigned';

export function planItemDoctorLabel(
  performingDoctorId: number | null | undefined,
  roster: ReadonlyArray<HandoffRosterEntry>,
): string {
  const id = performingDoctorId ?? null;
  if (id === null) return PLAN_ITEM_DOCTOR_UNASSIGNED_COPY;
  const match = roster.find((d) => d.id === id) ?? null;
  return match !== null ? match.name : `Doctor #${id}`;
}

/** The explicit incomplete-roster notice (renders whenever the wire flags
 *  truncation -- the surface says so; it never stays silent). */
export const HANDOFF_ROSTER_TRUNCATED_COPY =
  'Some doctors from other clinics are not shown. Ask your coordinator if the doctor you need is missing.';

// --- The longitudinal chart (DOCTOR-OS-EXPERIENCE-V1 C7/C8) -------------------

/** The ONE lifted case-history read state (one fetch, two renders: the
 *  earlier-visits rail + the treatment ledger share it). */
export type CaseHistoryLoadState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'error'; readonly text: string }
  | { readonly kind: 'ready'; readonly history: DoctorCaseHistory };

/** One treatment-ledger row (paper running-log parity C7): Date | Treatment
 *  done | Paid | Mode | Receipt. READ-ONLY derivation over the case-history
 *  wire; an absent fact renders the honest '-', never a fabricated value. */
export interface TreatmentLedgerRow {
  readonly caseSheetId: number;
  readonly dateIso: string;
  /** Completed procedure names joined ', ' -- '-' when none completed. */
  readonly treatmentsDone: string;
  /** Sum of the visit's payments, in the doctor money grammar ("Rs
   *  <en-IN digits>", digits from THE ONE minor-units formatter) -- '-'
   *  when none. */
  readonly paid: string;
  /** Distinct payment methods joined ', ' (humanized) -- '-' when none. */
  readonly modes: string;
  /** Payment ids as 'Payment #id' -- the product's honest receipt identity
   *  (no receipt-number series exists); '-' when no payment. */
  readonly receipts: string;
}

const LEDGER_DASH = '-';

/** Derive the ledger rows OLDEST FIRST (the paper log appends downward);
 *  the wire serves newest first. Pure + total. */
export function treatmentLedgerRows(
  entries: readonly CaseHistoryEntry[],
): readonly TreatmentLedgerRow[] {
  return [...entries].reverse().map((entry) => {
    const done = (entry.completedProcedures ?? []).filter((p) => p.trim() !== '');
    const payments = entry.payments ?? [];
    const paidMinor = payments.reduce((sum, p) => sum + p.amountMinorUnits, 0);
    const modes = [...new Set(payments.map((p) => p.method.replace(/_/g, ' ')))];
    const receipts = (entry.paymentIds ?? []).map((id) => `Payment #${id}`);
    return {
      caseSheetId: entry.caseSheetId,
      dateIso: entry.createdAt,
      treatmentsDone: done.length > 0 ? done.join(', ') : LEDGER_DASH,
      paid: payments.length > 0 ? rupeesFromMinorLabel(paidMinor) : LEDGER_DASH,
      modes: modes.length > 0 ? modes.join(', ') : LEDGER_DASH,
      receipts: receipts.length > 0 ? receipts.join(', ') : LEDGER_DASH,
    };
  });
}

// --- Cockpit masthead + rollup derivations ------------------------------------
// DOCTOR-COCKPIT-VISUAL-CONFORM-V1 round 2 (the founder coverage-map ruling on
// PR #529): three PURE derivations over reads the page ALREADY holds -- the
// lifted case-history read, the consult context's visit payments, and the
// sheet's plan lines. Zero new endpoints, zero writes. HONEST-ABSENCE LAW:
// when a fact is not exactly derivable (read pending, wire truncated, fenced
// content missing) the derivation answers null and the consumer renders the
// pre-round state -- never a fabricated or approximate count.

/** Mirror of api/routes/doctor-workspace.ts CASE_HISTORY_ENTRY_CAP (the wire
 *  serves at most this many in-scope entries, newest first). AT the cap the
 *  list may be truncated, so a count derived from it stops being a fact --
 *  the derivations below answer null there. */
export const CASE_HISTORY_WIRE_CAP = 50;

// Approved Wave 1 currency display: rupee glyph with Indian digit grouping.
// These presentation adapters preserve the canonical amounts and paise.
// Keep existing exported names for callers; the legacy Rs names no longer
// describe the visible prefix.

/** "₹ 24,000" from a whole-rupee value. */
export function rupeesLabel(wholeRupees: number): string {
  return `₹ ${wholeRupees.toLocaleString('en-IN')}`;
}

/** Keep the canonical minor-unit label intact; do not add a second currency prefix. */
export function rupeesFromMinorLabel(minorUnits: number): string {
  return formatMinorUnitsInr(minorUnits);
}

/** Normalize glyph spacing without changing the canonical amount or gap copy. */
export function rupeeGlyphToRs(display: string): string {
  return display.replace(/\u20B9\s*/g, '₹ ');
}

/** Replace only a leading INR display prefix; retain digits, paise, gap copy,
 *  non-INR currency codes, and mid-string prose unchanged. */
export function inrWordToRs(display: string): string {
  return display.replace(/^INR /, '₹ ');
}

/** The reference's done-chip grammar: "Done today" for a completion on the
 *  clinic's current IST day, else "Done <short date>". Pure over the served
 *  completedAt (never an invented date). DOCTOR-HOME-STATE-MODEL-V1 (F6a):
 *  the IST day comparison and the short date now read THE ONE IST owner
 *  (the previous local toLocaleDateString pair was the LES-126
 *  second-formatter class). */
export function doneChipLabel(completedAtIso: string, nowMs: number): string {
  const done = Date.parse(completedAtIso);
  if (Number.isNaN(done)) return 'Done';
  const istDay = (ms: number): string =>
    formatIstDateDdMmYyyy(new Date(ms).toISOString());
  if (istDay(done) === istDay(nowMs)) return 'Done today';
  return `Done ${formatIstShortDate(new Date(done).toISOString())}`;
}

/** C06: the patient's visit ordinal for the encounter on screen ("Visit N").
 *  N = served in-scope FINALIZED visits (excluding this appointment's own row
 *  when a finalized visit is re-opened) + the honest cross-clinic COUNT the
 *  wire already carries + 1 for the visit on screen. Null while the history
 *  loads, on error, or at the wire cap -- the chip stays absent. */
export function consultVisitOrdinal(
  history: CaseHistoryLoadState,
  currentAppointmentId: number,
): number | null {
  if (history.kind !== 'ready') return null;
  const entries = history.history.entries;
  if (entries.length >= CASE_HISTORY_WIRE_CAP) return null;
  const prior = entries.filter(
    (e) => e.appointmentId !== currentAppointmentId,
  ).length;
  return prior + history.history.crossClinicCount + 1;
}

/** C21-24 / C39: the ALL-VISITS paid sum in minor units -- the served
 *  case-history payments (excluding this appointment's own row) plus this
 *  visit's payments. Null unless the sum is EXACT: history ready, under the
 *  wire cap, and zero cross-clinic visits (their payments never reach this
 *  wire, so an "all visits" figure would be silently short -- the rollup
 *  then keeps the honest this-visit cell instead). */
export function paidAcrossVisitsMinorUnits(
  history: CaseHistoryLoadState,
  currentAppointmentId: number,
  thisVisitPayments: ReadonlyArray<{ readonly amountMinorUnits: number }>,
): number | null {
  if (history.kind !== 'ready') return null;
  const { entries, crossClinicCount } = history.history;
  if (entries.length >= CASE_HISTORY_WIRE_CAP || crossClinicCount > 0) {
    return null;
  }
  const past = entries
    .filter((e) => e.appointmentId !== currentAppointmentId)
    .reduce(
      (sum, e) =>
        sum +
        (e.payments ?? []).reduce((s, p) => s + p.amountMinorUnits, 0),
      0,
    );
  const current = thisVisitPayments.reduce(
    (s, p) => s + p.amountMinorUnits,
    0,
  );
  return past + current;
}

/** C09: the course step-line under the NEXT dock headline -- candidate-b
 *  grammar "Complete <procedure> <toothArea> - N of M remaining" over the
 *  plan already on the page. The step is the FIRST accepted line without
 *  completion evidence, in plan (wire) order -- the same order the ledger
 *  renders. Null when nothing is accepted or everything accepted is done
 *  (the ladder headline then owns the story alone). */
export function nextCourseSublineFrom(
  planItems: ReadonlyArray<{
    readonly procedureName: string;
    readonly toothArea: string | null;
    readonly status: string;
    readonly completedAt?: string | null;
  }>,
): string | null {
  const accepted = planItems.filter((i) => i.status === 'accepted');
  if (accepted.length === 0) return null;
  const remaining = accepted.filter((i) => i.completedAt == null);
  const next = remaining[0];
  if (next === undefined) return null;
  const where =
    next.toothArea !== null && next.toothArea.trim() !== ''
      ? ` ${next.toothArea.trim()}`
      : '';
  return `Complete ${next.procedureName}${where} - ${remaining.length} of ${accepted.length} remaining`;
}

// --- Day's-appointment law ----------------------------------------------------

/** The redirect notice when the opened visit is not today's (founder finding:
 *  #42 opened while #43 was the day's visit). */
export function todaysVisitNotice(
  context: Pick<DoctorConsultContext, 'todaysAppointmentId'>,
): string | null {
  if (context.todaysAppointmentId === null) return null;
  return `This is not today's visit for this patient - today's appointment is #${context.todaysAppointmentId}.`;
}

// --- Close act (R-1) ------------------------------------------------------------
//
// DOCTOR-ONE-CLOSE-AND-TRUST-V1: the three ceremonies (Finalize / Checkout /
// the duplicated pickers) collapse into ONE "Close the visit" act. The
// derivation + orchestration live in consult-close.ts.
// CHECKOUT-HANDOFF-AND-DOCTOR-CONTEXT-V1: the R-1 ungranted front-desk seam
// copy now rides the pre-close CONSEQUENCE line (closeHeadline,
// consult-close.ts) -- the old standalone FRONT_DESK_COMPLETES_COPY constant
// was removed with its render site (no dead exports; the #246 lesson).

// --- Visit-category options (SCOPE 3, DOCTOR-JOURNEY-COMPOSE-V1) ---------------

/**
 * The category-picker option labels for one visit-category field. The server
 * now resolves the effective vocabulary (clinic specialties when configured,
 * else the founder-seeded org keys clinical.complaint_categories /
 * clinical.diagnosis_categories) and serves it as {code,label} options; an
 * OLDER server omits the field and the mirror degrades to the legacy
 * clinicSpecialties list (byte-identical pre-package behavior). An EMPTY
 * resolved list means "no configured vocabulary" -- the caller renders CLEAN
 * ABSENCE (the field hides; raw "Not configured." never renders to a
 * doctor). Pure.
 */
export function visitCategoryOptionLabels(
  resolved: readonly { readonly label: string }[] | undefined,
  legacySpecialties: readonly string[],
): readonly string[] {
  if (resolved === undefined) return legacySpecialties;
  return resolved.map((o) => o.label);
}

// --- Patient-reported allergy provenance (ONE-TRUTH INTAKE, law 2a) ------------

/** The patient-reported allergy answer, shaped for the durable
 *  Allergies-and-alerts band's provenance line. */
export interface PatientReportedAllergyLine {
  /** e.g. "Yes - penicillin" or "None known" (labels from the served read). */
  readonly value: string;
  /** The served provenance label ("Patient-reported -- awaiting doctor
   *  review") -- never re-authored client-side. */
  readonly provenanceLabel: string;
}

/**
 * ONE-TRUTH INTAKE (ordering law 2a): the durable Allergies-and-alerts record
 * and the visit intake share ONE capture flow -- the patient-reported allergy
 * answer PREFILLS the durable band as read-only provenance context and the
 * doctor CONFIRMS by authoring the durable record (the band's existing
 * explicit Save/Confirm acts). This derivation is READ-side composition over
 * the page's ONE lifted questionnaire read; it never writes and never
 * auto-copies a patient answer into a doctor-authored field.
 *
 * Input is the lifted preconsult panel state (the same read the intake block
 * and safety strip consume). Only the clinical audience carries answers; the
 * 'allergies' question is the single true overlap with the durable band
 * (the 'medications' question asks CURRENT medications, not allergies -- it
 * never rides this line). Pure + exported so the 2a bridge is a pinned unit.
 */
export function patientReportedAllergyLine(state: {
  readonly kind: string;
  readonly view?: {
    readonly audience?: string;
    readonly provenanceLabel?: string;
    readonly answers?: readonly {
      readonly questionKey: string;
      readonly selectedLabels: readonly string[];
      readonly detailText: string | null;
    }[];
  };
}): PatientReportedAllergyLine | null {
  if (state.kind !== 'ready' || state.view === undefined) return null;
  const view = state.view;
  if (view.audience !== 'clinical') return null;
  const answer = (view.answers ?? []).find((a) => a.questionKey === 'allergies');
  if (answer === undefined) return null;
  const labels = answer.selectedLabels.filter((l) => l.trim() !== '');
  if (labels.length === 0) return null;
  const detail =
    answer.detailText !== null && answer.detailText.trim() !== ''
      ? ` - ${answer.detailText.trim()}`
      : '';
  return {
    value: `${labels.join(', ')}${detail}`,
    provenanceLabel: view.provenanceLabel ?? 'Patient-reported',
  };
}

// --- Clinical-answer truth (DOCTOR-CONSULT-DEMO-POLISH-V1, item 1) ----------

/**
 * The CLINICAL (non reason_for_visit) answers among a served answer list.
 * reason_for_visit is captured at the front desk and displayed separately
 * (the visit-header context line, preconsultReasonForVisit) -- it is never
 * one of the clinical questions the intake questions block asks. A read
 * whose only stored row is reason_for_visit has NOTHING patient-reported for
 * the doctor to review in that block, so any "answers exist" / "awaiting
 * doctor review" signal must be computed over THIS filtered set, never the
 * raw served list, or it over-claims patient-reported content that is not
 * there. Pure + exported so both the badge gate and the RELATED count read
 * the SAME derivation (never a second, drifting filter).
 */
export function clinicalReportedAnswers<
  T extends { readonly questionKey: string },
>(answers: readonly T[]): readonly T[] {
  return answers.filter((a) => a.questionKey !== 'reason_for_visit');
}

// --- RELATED rail: "Other appointments" truth (item 3) ----------------------

/** The ConsultRelatedRail appointments fact -- duck-typed here (never
 *  imported back from ConsultWorkspacePage.tsx, which imports THIS module)
 *  so the two stay structurally compatible without a circular import. */
export interface RelatedAppointmentsFactView {
  readonly todayCount: number;
  readonly upcomingCount: number;
  readonly nextLine: string | null;
}

/**
 * DOCTOR-CONSULT-DEMO-POLISH-V1 (item 3): derive the RELATED rail's "Other
 * appointments" fact from a doctor patient-lens read. todayAppointments is a
 * pure IST-date-window read (lib/adapters/supabase/patient-360-read-model.ts)
 * that does NOT exclude the appointment this consult IS -- left as-is, the
 * count could include the very visit the doctor is sitting inside (a live
 * symptom: "Appointments 0" read while inside Appointment #132). This
 * derivation excludes currentAppointmentId from the today count so the
 * number is truthful under the row's "Other appointments" label.
 * upcomingAppointments needs no such filter -- by construction (scheduledAt
 * >= tomorrow) it can never carry today's open appointment. Pure + exported
 * so the exclusion is a pinned unit, never a render-only behaviour that can
 * silently regress off a build.
 */
export function relatedAppointmentsFactFrom(
  lens: {
    readonly todayAppointments: readonly { readonly appointmentId: number }[];
    readonly upcomingAppointments: readonly unknown[];
    readonly nextAppointment: { readonly scheduledAt: string } | null;
  },
  currentAppointmentId: number | null,
): RelatedAppointmentsFactView {
  return {
    todayCount: lens.todayAppointments.filter(
      (a) => a.appointmentId !== currentAppointmentId,
    ).length,
    upcomingCount: lens.upcomingAppointments.length,
    nextLine:
      lens.nextAppointment !== null
        ? `Next: ${formatIstShortDate(lens.nextAppointment.scheduledAt)}`
        : null,
  };
}


/** An old baseline cannot settle an unanswered plan PUT. Only all submitted
 * fields on the independently read row confirm that attempted result. */
export function planPatchConfirmedByRead(row: ClinicalPlanItemView | undefined, patch: PlanItemUpdateInput): boolean {
  if (!row) return false;
  const entries = Object.entries(patch).filter(([, value]) => value !== undefined);
  if (!entries.length) return false;
  return entries.every(([key, value]) => {
    if (key === 'toothRefs') {
      const expected = [...new Set(value as readonly string[])].sort();
      const actual = [...new Set((row.toothRefs ?? []).map(ref => ref.toothCode))].sort();
      return JSON.stringify(expected) === JSON.stringify(actual);
    }
    const actual = (row as unknown as Record<string, unknown>)[key];
    return (actual ?? null) === value;
  });
}


/** V14g's quoted-price control uses existing whole-INR storage. The catalog
 * snapshot is paise; unknown/fractional defaults must not become invented INR. */
export function wholePlanListPrice(minor: number | null | undefined): number | null {
  return typeof minor === 'number' && Number.isSafeInteger(minor) && minor >= 0 && minor % 100 === 0 ? minor / 100 : null;
}
export const PLAN_DISCOUNT_UNITS = ['₹', '%'] as const;
export function planQuoteFromDiscount(list: number | null, input: string, mode: typeof PLAN_DISCOUNT_UNITS[number]): number | null {
  const raw = input.trim();
  if (list === null || !Number.isSafeInteger(list) || list < 0 || !/^(?:\d+(?:\.\d+)?|)$/.test(raw)) return null;
  const value = raw === '' ? 0 : Number(raw);
  if (!Number.isFinite(value) || value < 0 || (mode === '%' ? value > 100 : !Number.isSafeInteger(value) || value > list)) return null;
  const result = list - (mode === '%' ? Math.round(list * value / 100) : value);
  return Number.isSafeInteger(result) && result >= 0 ? result : null;
}


/** Collect only the estimate facts on the same admitted saved clinical reads.
 * Missing storage/source and partially read courses remain unknown. */
export function clinicalEstimateDiscounts(sheets: readonly Pick<CaseSheetClinicalView, 'id' | 'estimateAdjustment'>[]) {
  const rows: {caseSheetId:number;discountAmountInr:number}[] = [];
  for (const sheet of sheets) {
    if (sheet.estimateAdjustment?.status !== 'available') return null;
    const adjustment = sheet.estimateAdjustment.adjustment;
    if (adjustment) rows.push({caseSheetId:sheet.id,discountAmountInr:adjustment.discountAmountInr});
  }
  return rows;
}

/** V14g estimate projection. Saved course discounts apply only to their owning
 * sheet and accepted work. Unknown money is never silently summed as zero. */
export function consultPlanEstimate(rows: readonly {caseSheetId:number; status:string; amount:number|null; listMinor?:number|null}[],
  discounts: readonly {caseSheetId:number; discountAmountInr:number}[] | null,
  todayMinor: number|null, courseKnown = true) {
  const minor = (n:number|null|undefined) => typeof n==='number' && Number.isFinite(n) && n>=0 && Number.isSafeInteger(n*100) ? n*100 : null;
  const sum = (values:readonly (number|null)[]) => { if(values.some(value=>value===null))return null; const total=values.reduce<number>((total,value)=>total+value!,0);return Number.isSafeInteger(total)?total:null; };
  const group = (status:string) => { const matching=rows.filter(row=>row.status===status); return {count:matching.length,minor:sum(matching.map(row=>minor(row.amount)))}; };
  const accepted=group('accepted'), advised=group('advised'), declined=group('declined'), deferred=group('deferred');
  const unansweredRows=rows.filter(row=>!['accepted','advised','declined','deferred'].includes(row.status));
  const unanswered={count:unansweredRows.length,minor:sum(unansweredRows.map(row=>minor(row.amount)))};
  const live=rows.filter(row=>row.status!=='declined'&&row.status!=='deferred');
  const gross=courseKnown?sum(live.map(row=>minor(row.amount))):null;
  const list=courseKnown?sum(live.map(row=>typeof row.listMinor==='number'&&Number.isSafeInteger(row.listMinor)&&row.listMinor>=0?row.listMinor:null)):null;
  const sheetIds=new Set(rows.map(row=>row.caseSheetId));
  const seen=new Set<number>();
  let extra:number|null=discounts===null||!courseKnown?null:0;
  for(const discount of discounts??[]) {
    if(!sheetIds.has(discount.caseSheetId)) continue;
    const value=minor(discount.discountAmountInr);
    const owningAccepted=sum(rows.filter(row=>row.caseSheetId===discount.caseSheetId&&row.status==='accepted').map(row=>minor(row.amount)));
    if(seen.has(discount.caseSheetId)||value===null||owningAccepted===null||value>owningAccepted) extra=null;
    else if(extra!==null) extra+=value;
    seen.add(discount.caseSheetId);
  }
  const subtract=(value:number|null)=>value===null||extra===null||extra>value?null:value-extra;
  const acceptedNet=courseKnown?subtract(accepted.minor):null;
  const estimate=subtract(gross);
  const today=typeof todayMinor==='number'&&Number.isSafeInteger(todayMinor)&&todayMinor>=0?todayMinor:null;
  return {accepted,advised,declined,deferred,unanswered,gross,list,extra,acceptedNet,estimate,today,
    agreed:sum([acceptedNet,today]),total:sum([estimate,today])};
}


/** Discount preview only. The protected fee writer owns authority and the saved amount. */
export function consultationAmountFromDiscount(original: number, raw: string, mode: '₹' | '%'): number | null {
  if (!Number.isSafeInteger(original) || original <= 0) return null;
  const text = raw.trim();
  if (text === '' || /^0+(?:\.0{1,2})?$/.test(text)) return original;
  // Reuse the exact decimal parser: in percent mode its result is basis points.
  const units = parseInrToMinorUnits(text);
  if (units === null || (mode === '%' && units > 10000)) return null;
  const off = mode === '₹' ? units : Number((BigInt(original) * BigInt(units) + 5000n) / 10000n);
  return off <= original ? original - off : null;
}

export const RETURN_REQUEST_CHOICES = [
  {code:'in_3_days',label:'In 3 days'}, {code:'in_1_week',label:'In 1 week'},
  {code:'in_2_weeks',label:'In 2 weeks'}, {code:'other',label:'Other'},
  {code:'after_treatment',label:'After treatment ends'}, {code:'none',label:'No follow-up needed'},
] as const;

/** The frontend ships independently. Mirror the wire's anchored calendar
 * calculation, with cross-boundary tests against the server implementation. */
export function returnRequestDate(request: ClinicalReturnRequestView | null | undefined): string | null {
  if(!request || request.timing==='none' || request.timing==='after_treatment')return null;
  if(request.timing==='other' && request.onDate)return request.onDate;
  const preset={in_3_days:3,in_1_week:7,in_2_weeks:14};
  const interval=request.timing==='other'?request.interval:{count:preset[request.timing],unit:'days'};
  if(!interval || !Number.isSafeInteger(interval.count) || interval.count<1)return null;
  const date=new Date(request.basedOnDate+'T00:00:00.000Z');
  if(interval.unit==='months')date.setUTCMonth(date.getUTCMonth()+interval.count);
  else date.setUTCDate(date.getUTCDate()+interval.count*(interval.unit==='weeks'?7:1));
  if(!Number.isFinite(date.getTime()))return null;
  const value=date.toISOString();return /^\d{4}-/.test(value)?value.slice(0,10):null;
}
export function returnRequestsEqual(a: ClinicalReturnRequestView | null | undefined,b: ClinicalReturnRequestView | null | undefined): boolean {
  if(!a||!b)return !a&&!b;
  return a.version===b.version && a.timing===b.timing && a.basedOnDate===b.basedOnDate && a.onDate===b.onDate &&
    a.interval?.count===b.interval?.count && a.interval?.unit===b.interval?.unit && a.forPlanItemId===b.forPlanItemId &&
    a.details===b.details && a.requestedDoctorId===b.requestedDoctorId && a.durationMinutes===b.durationMinutes;
}
export function returnRequestProblem(request: ClinicalReturnRequestView | null | undefined, _saved: ClinicalReturnRequestView | null | undefined, today: string): string | null {
  if(!request)return null;
  if(request.interval && (!Number.isSafeInteger(request.interval.count)||request.interval.count<1))return 'Enter a whole number greater than zero.';
  const due=returnRequestDate(request);
  if(request.timing==='other' && !request.onDate && !request.interval)return null; // incomplete draft, never a complete Finish
  if(request.timing==='none'||request.timing==='after_treatment')return null;
  if(!due || !/^\d{4}-\d{2}-\d{2}$/.test(due) || !Number.isFinite(Date.parse(due+'T00:00:00.000Z')) || new Date(due+'T00:00:00.000Z').toISOString().slice(0,10)!==due)return 'Choose a valid return date.';
  return due<=today?'The return date must be after today. Choose a later date.':null;
}
export function returnRequestSummary(request: ClinicalReturnRequestView | null | undefined): string | null {
  if(!request)return null;
  if(request.timing==='none')return 'No return visit needed. Existing appointments stay as they are.';
  if(request.timing==='after_treatment')return 'Return requested when the treatment ends.';
  const due=returnRequestDate(request);
  return due?`Return requested on ${returnDayLabel(due)}.`:'Return date not set.';
}

/** One readable Return day for the booking card, Finish and the patient copy:
 * weekday and date, with the year only when it is not the clinic's year. */
export function returnDayLabel(isoOrDay: string, today: string = clinicToday()): string {
  const day=isoOrDay.length===10?isoOrDay:istCalendarDayYmd(Date.parse(isoOrDay));
  return `${new Intl.DateTimeFormat('en-GB',{weekday:'short',timeZone:'UTC'}).format(new Date(day+'T12:00:00Z'))} ${formatIstShortDate(day)}${day.slice(0,4)!==today.slice(0,4)?' '+day.slice(0,4):''}`;
}
export function returnTimeLabel(iso: string): string {
  const m=istMinutesOfDay(iso);return m===null?'':`${String(Math.floor(m/60)).padStart(2,'0')}:${String(m%60).padStart(2,'0')}`;
}
/** A saved, active Return appointment as the frozen V14g Finish names it. */
export function bookedReturnLine(booking: { appointmentTime: string; durationMinutes: number }, clinicName: string, doctorName: string | null): string {
  const end=new Date(Date.parse(booking.appointmentTime)+booking.durationMinutes*60000).toISOString();
  return `Booked · ${returnDayLabel(booking.appointmentTime)}, ${returnTimeLabel(booking.appointmentTime)}–${returnTimeLabel(end)} · ${clinicName}${doctorName?` · ${doctorName}`:''}`;
}
export const activeReturnBooking = (status: string | null | undefined): boolean => status === 'booked' || status === 'confirmed';

export function finishReviewsEqual(a:ClinicalFinishReviewView|null|undefined,b:ClinicalFinishReviewView|null|undefined):boolean {
 if(a==null||b==null)return a==null&&b==null;
 return a.version===b.version&&a.outcome===b.outcome&&a.googleReviewAsked===b.googleReviewAsked;
}

/** Today's admitted intake reason for THIS appointment (the saved print's
 *  fallback when no complaint is saved yet). Display only: null for another
 *  appointment, an identity under review, a non-clinical view or no answer. */
export function todaysIntakeReason(view: DoctorPreconsultView | null | undefined, appointmentId: number): string | null {
  if (!view || view.audience !== 'clinical' || view.underIdentityReview || view.appointmentId !== appointmentId) return null;
  return view.answers.find(answer => answer.questionKey === 'reason_for_visit')?.detailText?.trim() || null;
}

/** The consultation doctor on today's fee line (frozen V14g: the doctor who
 *  conducts the consultation, stamped as the conversion doctor at acceptance).
 *  A saved stamp always wins. Before any acceptance an open visit names the
 *  doctor conducting it; a finished visit with no stamp stays "Not recorded"
 *  (an old unknown author is never backfilled). */
export function consultationDoctorLabel(saved: string, sheetStatus: string | null | undefined, conductingDoctorName: string | null | undefined): string {
  if (saved !== 'Not recorded' || sheetStatus === 'finalized') return saved;
  const name = conductingDoctorName?.trim();
  return name ? `${name} (you)` : saved;
}
